import React from 'react';
import { Chessboard } from 'react-chessboard';
import { DataContext } from '../DataProvider';
import styled from 'styled-components';

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function ChessBoardWrapper() {
  const {
    fen,
    makeMove,
    makeTrainingMove,
    currentRepertoireId,
    currentMode,
    trainingState,
    currentNode,
    boardOrientation,
    toggleBoardOrientation,
    highlightedSquares,
    customArrows,
    showHint,
    startTraining,
    stopTraining,
  } = React.useContext(DataContext);

  function onDrop(sourceSquare, targetSquare) {
    if (!currentRepertoireId) {
      alert('Please select or create a repertoire first.');
      return false;
    }

    if (currentMode === 'trainer' && trainingState.isActive) {
      const chessTurn = fen.includes(' w ') ? 'white' : 'black';
      const userColor = trainingState.userColor;

      if (chessTurn !== userColor) {
        return false;
      }

      const move = makeTrainingMove({
        from: sourceSquare,
        to: targetSquare,
      });

      if (move === null) {
        return false;
      }

      return true;
    } else {
      const move = makeMove({
        from: sourceSquare,
        to: targetSquare,
      });

      if (move === null) {
        return false;
      }

      return true;
    }
  }

  const isUserTurn = () => {
    if (!trainingState.isActive) return false;
    const chessTurn = fen.includes(' w ') ? 'white' : 'black';
    return chessTurn === trainingState.userColor;
  };

  const userTurn = isUserTurn();
  const [toastDismissed, setToastDismissed] = React.useState(false);

  const isFirstMoveAsWhite =
    trainingState.isActive &&
    trainingState.userColor === 'white' &&
    fen === START_FEN;

  React.useEffect(() => {
    setToastDismissed(false);
  }, [trainingState.isActive]);

  return (
    <BoardContainer>
      <BoardHeader>
        <FlipButton onClick={toggleBoardOrientation}>
          {boardOrientation === 'white' ? 'View from black' : 'View from white'}
        </FlipButton>
      </BoardHeader>

      {!currentRepertoireId && (
        <NoRepertoireBox>
          <NoRepertoireText>Welcome to the study board</NoRepertoireText>
          <NoRepertoireSubtext>
            Create your first repertoire or select an existing one to start
            building your opening knowledge
          </NoRepertoireSubtext>
        </NoRepertoireBox>
      )}

      {currentMode === 'trainer' && isFirstMoveAsWhite && !toastDismissed && (
        <FirstMoveToast>
          Your move
          <ToastDismiss onClick={() => setToastDismissed(true)}>&times;</ToastDismiss>
        </FirstMoveToast>
      )}

      <BoardWithSide>
        <Chessboard
          id='RepertoireBoard'
          position={fen || START_FEN}
          boardOrientation={boardOrientation}
          onPieceDrop={onDrop}
          customSquareStyles={highlightedSquares}
          customArrows={customArrows}
          customDarkSquareStyle={{
            backgroundColor: '#D97757',
          }}
          customLightSquareStyle={{
            backgroundColor: '#FFF5EB',
          }}
          customBoardStyle={{
            borderRadius: '4px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
          }}
          customDropSquareStyle={{
            boxShadow: 'inset 0 0 0 3px rgba(193, 95, 60, 0.4)',
          }}
          customPremoveDarkSquareStyle={{ backgroundColor: '#C46B4F' }}
          customPremoveLightSquareStyle={{ backgroundColor: '#FFE8D6' }}
        />
        {currentMode === 'trainer' && (
          <SidePanel>
            {trainingState.isActive && (
              <SideButton
                onClick={showHint}
                disabled={!trainingState.nextMove}
                title='Show hint for next move'
              >
                <SideEmoji>{'\uD83D\uDE48'}</SideEmoji>
                Reveal
                <br />
                hint
              </SideButton>
            )}
            <SideButton
              onClick={trainingState.isActive
                ? stopTraining
                : () => startTraining(boardOrientation)}
              $secondary={trainingState.isActive}
            >
              <SideEmoji>{trainingState.isActive ? '\uD83D\uDED1' : '\uD83D\uDEA6'}</SideEmoji>
              {trainingState.isActive ? 'Stop' : 'Begin'}
            </SideButton>
          </SidePanel>
        )}
      </BoardWithSide>

      {currentMode === 'trainer' && trainingState.isActive && (
        <TrainingInfoBox>
          <TrainingProgress>
            <ProgressBar>
              <ProgressFill $progress={trainingState.progress} />
            </ProgressBar>
            <ProgressText>{trainingState.progress}%</ProgressText>
          </TrainingProgress>
          {currentNode && currentNode.comment && (
            <PositionNote>{currentNode.comment}</PositionNote>
          )}
        </TrainingInfoBox>
      )}
    </BoardContainer>
  );
}

export default ChessBoardWrapper;

const BoardContainer = styled.div`
  width: 100%;
  max-width: 98vh;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BoardWithSide = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const SidePanel = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SideButton = styled.button`
  padding: 14px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.3;
  text-align: center;
  cursor: pointer;
  color: ${(props) => props.$secondary ? 'var(--text-soft)' : 'var(--accent-strong)'};
  background: var(--surface-strong);
  border: 1px solid rgba(159, 109, 76, 0.18);
  border-radius: 16px;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--accent-ghost);
    transform: translateY(-1px);
    box-shadow: 0 14px 26px rgba(86, 62, 43, 0.16);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background-color: rgba(255, 249, 241, 0.75);
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SideEmoji = styled.div`
  font-size: 1.64rem;
  line-height: 1;
  margin-bottom: 4px;
`;

const BoardHeader = styled.div`
  display: flex;
  justify-content: center;
`;

const FlipButton = styled.button`
  padding: 11px 18px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  color: var(--accent-strong);
  background: var(--surface-strong);
  border: 1px solid var(--border);
  border-radius: 999px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--accent-ghost);
    transform: translateY(-1px);
    box-shadow: 0 12px 24px rgba(86, 62, 43, 0.12);
  }

  &:active {
    transform: translateY(0);
  }
`;

const TrainingInfoBox = styled.div`
  width: 100%;
  padding: 14px 18px;
  box-sizing: border-box;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: var(--shadow-md);
`;

const TrainingProgress = styled.div`
  margin-bottom: 4px;
`;

const ProgressBar = styled.div`
  width: 100%;
  margin: 0 auto 6px;
  height: 8px;
  background-color: var(--accent-soft);
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: var(--accent);
  width: ${(props) => props.$progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--text-soft);
`;

const FirstMoveToast = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 8px 16px;
  font-size: 0.82rem;
  color: var(--text-soft);
  background: rgba(255, 249, 241, 0.92);
  border: 1px dashed rgba(139, 90, 43, 0.3);
  border-radius: 999px;
  align-self: center;
`;

const ToastDismiss = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-faint);
  font-size: 1rem;
  line-height: 1;
  padding: 0 2px;

  &:hover {
    color: var(--text-soft);
  }
`;

const PositionNote = styled.div`
  text-align: center;
  font-size: 0.84rem;
  color: var(--text-soft);
  margin-top: 12px;
  padding: 10px 12px;
  background-color: rgba(255, 249, 241, 0.8);
  border: 1px solid var(--border);
  border-radius: 14px;
`;

const NoRepertoireBox = styled.div`
  width: 100%;
  min-height: 110px;
  padding: 18px;
  box-sizing: border-box;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  box-shadow: var(--shadow-md);
`;

const NoRepertoireText = styled.h3`
  margin-bottom: 8px;
  color: var(--text);
`;

const NoRepertoireSubtext = styled.p`
  font-size: 0.9rem;
  color: var(--text-soft);
  max-width: 30ch;
`;
